import type { Camera } from "../engine/camera.js";
import type { AtmosphereQuality } from "../engine/atmosphere.js";
import { LightningPresentation, lightningFlashAt, precipitationCounts, type PrecipitationFrame } from "./weather.js";

const precipitationVertex = `#version 300 es
precision highp float;
uniform mat4 uViewProjection;
uniform vec3 uCenter;
uniform vec3 uRight;
uniform vec3 uUp;
uniform vec2 uWind;
uniform vec2 uWindOffset;
uniform float uTime;
uniform float uKind;
uniform float uGround;
out vec2 vUv;
out float vFade;
float hash(float x) { return fract(sin(x * 127.1 + 311.7) * 43758.5453); }
void main() {
  int vertex = gl_VertexID;
  vec2 corners[6] = vec2[6](vec2(-1,-1),vec2(1,-1),vec2(1,1),vec2(-1,-1),vec2(1,1),vec2(-1,1));
  vec2 corner = corners[vertex];
  float id = float(gl_InstanceID) + uKind * 1999.0;
  vec3 seed = vec3(hash(id + 1.0), hash(id + 2.0), hash(id + 3.0));
  bool snow = uKind > 0.5 && uKind < 1.5;
  bool hail = uKind > 1.5;
  float speed = snow ? 1.65 : (hail ? 16.0 : 24.0);
  vec3 velocity = vec3(uWind.x, -speed, uWind.y);
  vec3 extent = vec3(44.0, 26.0, 44.0);
  vec3 displacement = vec3(uWindOffset.x, -speed * uTime, uWindOffset.y);
  vec3 world = uCenter + (fract(seed + displacement / extent) - 0.5) * extent;
  if (snow) world.xz += vec2(sin(uTime + id), cos(uTime * 0.7 + id)) * 0.5;
  vec3 longAxis = snow || hail ? uUp : normalize(velocity);
  float width = snow ? 0.075 : (hail ? 0.042 : 0.019);
  float streakLength = snow ? width : (hail ? width * 1.25 : 0.45 + seed.y * 0.40);
  world += uRight * corner.x * width + longAxis * corner.y * streakLength;
  vUv = corner;
  vFade = smoothstep(0.0, 2.0, world.y - uGround) * (1.0 - smoothstep(16.0, 22.0, length(world.xz - uCenter.xz)));
  gl_Position = uViewProjection * vec4(world, 1.0);
}`;
const precipitationFragment = `#version 300 es
precision highp float;
uniform float uKind;
uniform float uLight;
uniform float uOpacity;
in vec2 vUv;
in float vFade;
out vec4 outColor;
void main() {
  float shape = uKind > 0.5 ? 1.0 - smoothstep(0.35, 1.0, length(vUv)) : (1.0 - abs(vUv.x)) * (1.0 - abs(vUv.y) * 0.55);
  vec3 color = uKind < 0.5 ? vec3(0.56,0.67,0.75) : (uKind < 1.5 ? vec3(0.78,0.83,0.86) : vec3(0.87,0.92,0.96));
  float alpha = shape * vFade * uOpacity * (uKind < 0.5 ? 0.46 : 0.85);
  outColor = vec4(color * mix(0.32, 1.0, uLight), alpha);
}`;
const boltVertex = `#version 300 es
precision highp float;
layout(location=0) in vec3 aPosition;
uniform mat4 uViewProjection;
void main() { gl_Position = uViewProjection * vec4(aPosition, 1.0); }`;
const boltFragment = `#version 300 es
precision highp float;
uniform vec4 uColor;
out vec4 outColor;
void main() { outColor = uColor; }`;

function program(gl: WebGL2RenderingContext, vertex: string, fragment: string): WebGLProgram {
  const shaders: WebGLShader[] = [];
  const result = gl.createProgram();
  if (!result) throw new Error("weather program allocation");
  try {
    for (const [kind, source] of [[gl.VERTEX_SHADER, vertex], [gl.FRAGMENT_SHADER, fragment]] as const) {
      const shader = gl.createShader(kind);
      if (!shader) throw new Error("weather shader allocation");
      shaders.push(shader); gl.shaderSource(shader, source); gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? "weather shader compilation");
      gl.attachShader(result, shader);
    }
    gl.linkProgram(result);
    if (!gl.getProgramParameter(result, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(result) ?? "weather program link");
    return result;
  } catch (error) { gl.deleteProgram(result); throw error; }
  finally { for (const shader of shaders) gl.deleteShader(shader); }
}

/** Constant-size GPU resources; zero per-particle JavaScript state or network entities. */
export class WeatherRenderer {
  readonly lightning = new LightningPresentation();
  readonly metrics = { precipitationDrawCalls: 0, precipitationCount: 0, precipitationCpuMs: 0, lightningDrawCalls: 0, lightningVisibleBoltCount: 0, resourceBytes: 5184, available: false };
  private precipitation: WebGLProgram | undefined;
  private bolt: WebGLProgram | undefined;
  private vao: WebGLVertexArrayObject | null = null;
  private boltVao: WebGLVertexArrayObject | null = null;
  private buffer: WebGLBuffer | null = null;
  private readonly boltVertices = new Float32Array(36 * 6 * 3);
  private readonly uniforms: Record<string, WebGLUniformLocation | null> = {};
  private readonly boltUniforms: Record<string, WebGLUniformLocation | null> = {};
  private lastWindEpoch = 0;
  private readonly windOffset: [number, number] = [0, 0];
  constructor(private readonly gl: WebGL2RenderingContext) {
    try {
      this.precipitation = program(gl, precipitationVertex, precipitationFragment);
      this.bolt = program(gl, boltVertex, boltFragment);
      this.vao = gl.createVertexArray(); this.boltVao = gl.createVertexArray(); this.buffer = gl.createBuffer();
      if (!this.vao || !this.boltVao || !this.buffer) throw new Error("weather buffer allocation");
      for (const name of ["uViewProjection", "uCenter", "uRight", "uUp", "uWind", "uWindOffset", "uTime", "uKind", "uGround", "uLight", "uOpacity"]) this.uniforms[name] = gl.getUniformLocation(this.precipitation, name);
      for (const name of ["uViewProjection", "uColor"]) this.boltUniforms[name] = gl.getUniformLocation(this.bolt, name);
      gl.bindVertexArray(this.boltVao); gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.boltVertices.byteLength, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
      gl.bindVertexArray(null); this.metrics.available = true;
    } catch (error) { this.dispose(); console.warn("Weather effects unavailable; gameplay continues", error instanceof Error ? error.message : "allocation failed"); }
  }

  draw(camera: Camera, frame: PrecipitationFrame, quality: AtmosphereQuality, serverEpoch: number, ground: number, enabled = true): void {
    const started = performance.now();
    const elapsed = this.lastWindEpoch ? Math.max(0, Math.min(0.5, (serverEpoch - this.lastWindEpoch) / 1000)) : 0;
    this.lastWindEpoch = serverEpoch;
    this.windOffset[0] = (this.windOffset[0] + Math.cos(frame.windDirectionRadians) * frame.windSpeed * 0.34 * elapsed) % 44;
    this.windOffset[1] = (this.windOffset[1] + Math.sin(frame.windDirectionRadians) * frame.windSpeed * 0.34 * elapsed) % 44;
    this.metrics.precipitationDrawCalls = 0; this.metrics.precipitationCount = 0; this.metrics.lightningDrawCalls = 0;
    this.metrics.lightningVisibleBoltCount = 0;
    if (!this.metrics.available || !this.precipitation || !this.bolt || !enabled) { this.metrics.precipitationCpuMs = 0; return; }
    const gl = this.gl;
    const right = [camera.view[0]!, camera.view[4]!, camera.view[8]!] as const;
    gl.enable(gl.DEPTH_TEST); gl.depthMask(false); gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(this.precipitation); gl.bindVertexArray(this.vao);
    const u = this.uniforms;
    gl.uniformMatrix4fv(u.uViewProjection!, false, camera.viewProjection);
    gl.uniform3f(u.uCenter!, camera.position[0], camera.position[1] + 7, camera.position[2]);
    gl.uniform3f(u.uRight!, ...right);
    gl.uniform3f(u.uUp!, camera.view[1]!, camera.view[5]!, camera.view[9]!);
    gl.uniform2f(u.uWind!, Math.cos(frame.windDirectionRadians) * frame.windSpeed * 0.34, Math.sin(frame.windDirectionRadians) * frame.windSpeed * 0.34);
    gl.uniform2f(u.uWindOffset!, ...this.windOffset);
    gl.uniform1f(u.uTime!, performance.now() / 1000); gl.uniform1f(u.uGround!, ground);
    gl.uniform1f(u.uLight!, Math.min(1, Math.max(0, frame.daylight)));
    const counts = precipitationCounts(frame, quality);
    for (let kind = 0; kind < 3; kind++) {
      const count = counts[kind]!;
      if (!count) continue;
      gl.uniform1f(u.uKind!, kind); gl.uniform1f(u.uOpacity!, Math.min(1, count / 32));
      gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, count);
      this.metrics.precipitationDrawCalls++; this.metrics.precipitationCount += count;
    }
    gl.useProgram(this.bolt); gl.bindVertexArray(this.boltVao);
    gl.uniformMatrix4fv(this.boltUniforms.uViewProjection!, false, camera.viewProjection);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    for (const item of this.lightning.active) {
      const alpha = lightningFlashAt(item.event, serverEpoch);
      if (alpha <= 0.01) continue;
      const m = camera.viewProjection;
      for (let point = 0; point < item.segments.length; point += 3) {
        const x = item.segments[point]!, y = item.segments[point + 1]!, z = item.segments[point + 2]!;
        const w = m[3]! * x + m[7]! * y + m[11]! * z + m[15]!;
        const px = m[0]! * x + m[4]! * y + m[8]! * z + m[12]!;
        const py = m[1]! * x + m[5]! * y + m[9]! * z + m[13]!;
        if (w > 0 && Math.abs(px) < w && Math.abs(py) < w) { this.metrics.lightningVisibleBoltCount++; break; }
      }
      for (const width of [0.30, 0.065]) {
        let offset = 0;
        for (let segment = 0; segment < item.segments.length; segment += 6) {
          for (const [end, sign] of [[0, -1], [0, 1], [3, 1], [0, -1], [3, 1], [3, -1]] as const) {
            for (let axis = 0; axis < 3; axis++) this.boltVertices[offset++] = item.segments[segment + end + axis]! + right[axis]! * width * sign;
          }
        }
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer); gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.boltVertices, 0, offset);
        gl.uniform4f(this.boltUniforms.uColor!, width > 0.1 ? 0.4 : 0.92, width > 0.1 ? 0.5 : 0.96, 1, Math.min(1, alpha) * (width > 0.1 ? 0.24 : 0.96));
        gl.drawArrays(gl.TRIANGLES, 0, offset / 3); this.metrics.lightningDrawCalls++;
      }
    }
    gl.bindVertexArray(null); gl.disable(gl.BLEND); gl.enable(gl.CULL_FACE); gl.depthMask(true);
    this.metrics.precipitationCpuMs = performance.now() - started;
  }
  dispose(): void {
    const gl = this.gl;
    if (this.precipitation) gl.deleteProgram(this.precipitation);
    if (this.bolt) gl.deleteProgram(this.bolt);
    gl.deleteVertexArray(this.vao); gl.deleteVertexArray(this.boltVao); gl.deleteBuffer(this.buffer);
    this.metrics.available = false;
  }
}
